import { IConfig, IKeywords, ISlot, SizeType } from '../../uanc.types';
import { isArrayType, isEmpty, isStringType, removeWhitespace } from '../../helpers/js-utils';
import { logToKibana } from '../log-to-kibana';
import { AD_SERVERS, SMART_LL } from '../../configs/constants';
import DebugUANC from '../../core/version/debug';

/**
 * Przygotowuje pełny obiekt konfiguracji. Uzupełnia przekazany obiekt i ustawia niezbędne pola z domyślnymi wartościami.
 *
 * @param {Partial<IConfig>} config - niepełna konfiguracja do uzupełnienia
 * @returns {IConfig} przygotowana pełna konfiguracja z uzupełnionymi polami
 */
const prepareFullConfig = (config: Partial<IConfig>): IConfig => {
    const defaultState: IConfig = {
        adPath: '',
        cid: '',
        master: null,
        pid: '',
        prebidsConfig: {
            buckets: [],
            currency: 'USD',
            timeout: 700,
            enabled: true,
            priceGranularity: 'dense',
            enabledOrder: false,
            priority: [],
        },
        priceBandRange: null,
        refreshAds: null,
        slots: [],
        url: '',
        keywords: {},
        adServers: [],
        timeoutCriteo: 700,
    };

    return { ...defaultState, ...config };
};

/**
 * Funkcja sprawdza podstawowe pola konfiguracji (adPath, cid, master)
 *
 * @param {IConfig} config - konfiguracja pobrana z ADC lub domyślna
 * @returns {Partial<IConfig>} przygotowana konfiguracja pod względem pól podstawywych
 */
const prepareBasicFields = (config: IConfig): Partial<IConfig> => {
    const updatedConfig: Partial<IConfig> = { ...config };
    if (isStringType(config.adPath) && config.adPath) {
        updatedConfig.adPath = removeWhitespace(config.adPath);
    } else {
        delete updatedConfig.adPath;
    }

    if (isStringType(config.cid) && config.cid) {
        updatedConfig.cid = removeWhitespace(config.cid);
    } else {
        delete updatedConfig.cid;
    }

    if (isStringType(config.pid) && config.pid) {
        updatedConfig.pid = removeWhitespace(config.pid);
    } else {
        delete updatedConfig.pid;
    }

    if (isStringType(config.master) && config.master) {
        updatedConfig.master = removeWhitespace(config.master);
    } else {
        delete updatedConfig.master;
    }

    if (isStringType(config.priceBandRange) && config.priceBandRange) {
        updatedConfig.priceBandRange = removeWhitespace(config.priceBandRange);
    } else {
        delete updatedConfig.priceBandRange;
    }

    return updatedConfig;
};

/**
 * Ustawia keywords w konfiguracji na podstawie słów kluczowych otrzymanych z serwisu (settings/exSettings).
 *
 * @param {IKeywords} keywordsADC - keywords ustawione w adSet, otrzymane z ADC
 * @returns {IKeywords} słowa kluczowe
 */
export const prepareKeywords = (keywordsADC: IKeywords = {}): IKeywords => {
    const { exSettings } = window.Inpl.Ad;
    const keywordsSettings = exSettings?.keywords || {};
    if (isEmpty(keywordsSettings)) return keywordsADC;

    const keywords: IKeywords = {};
    const { DFP, ADOCEAN } = keywordsSettings;

    if (DFP && !isEmpty(DFP)) keywords.DFP = 'DFP' in keywordsADC && !isEmpty(keywordsADC.DFP) ? keywordsADC.DFP + ',' + DFP : DFP;
    if (ADOCEAN && !isEmpty(ADOCEAN))
        keywords.ADOCEAN = 'ADOCEAN' in keywordsADC && !isEmpty(keywordsADC.ADOCEAN) ? keywordsADC.ADOCEAN + ',' + ADOCEAN : ADOCEAN;

    return keywords;
};

/**
 * Walidacja sizes slotu.
 *
 * @param {SizeType[]} sizes - rozmiary slotu
 * @returns {SizeType[]} sprawdzone rozmiary
 */
const validateSizesSlot = (sizes: SizeType[]): SizeType[] => {
    const { UANC } = window.Inpl.Ad;

    return sizes.map((size: SizeType) => {
        if (size.length !== 2) {
            UANC instanceof DebugUANC && UANC.warn('validateSizesSlot - invalid structure of "sizes" object.');
        }

        return size.map((subSize) => {
            if (typeof subSize === 'number') {
                return subSize;
            } else {
                const converted = Number(subSize);
                if (isNaN(converted)) {
                    UANC instanceof DebugUANC && UANC.warn(`validateSizesSlot - Element "${subSize}" cannot be converted to a number`);
                }
                return converted;
            }
        }) as [number, number];
    });
};

/**
 * Sprawdza ustawienia slotów otrzymanych z ADC.
 *
 * @param {ISlot[]} slots - sloty otrzymane z ADC
 * @returns {ISlot[]} sprawdzone sloty
 */
const prepareSlots = (slots: ISlot[]): ISlot[] => {
    return slots.map((slot: ISlot) => {
        if (!slot.adServer) {
            slot.disable = true;
        } else {
            slot.adServer = slot.adServer.replace(/\s/g, '').toUpperCase() as AD_SERVERS.DFP | AD_SERVERS.ADOCEAN;
        }

        if (!slot.name) {
            slot.disable = true;
        } else {
            slot.name = slot.name.replace(/\s/g, '');
        }

        if (slot.mapping && slot.adServer === 'DFP') {
            if (isStringType(slot.mapping) && !isArrayType(slot.mapping)) {
                slot.mapping = JSON.parse(slot.mapping);
            } else {
                logToKibana({
                    message: `The mapping format for ${slot.name} is not correct`,
                });
            }
        } else {
            slot.mapping = null;
        }

        slot.sizes = isArrayType(slot.sizes) && slot.sizes.length && slot.adServer !== 'ADOCEAN' ? validateSizesSlot(slot.sizes) : null;

        return slot;
    });
};

/**
 * Weryfikuje ustawienia slotów i konfigurację reklam.
 *
 * @param {IConfig} config - konfiguracja pobrana z ADC lub domyślna
 * @returns {Promise<IConfig>} przygotowana konfiguracja
 */
export const configService = async (config: IConfig): Promise<IConfig> => {
    if (!('slots' in config) || !isArrayType(config.slots)) {
        // nie wyrzucamy wyjątku i nie przerywamy działania - pluginy ładowane w późniejszym etapie loadApp
        logToKibana({
            message: 'Brak zainicjowanych slotów',
        });
    }

    const updatedConfig: Partial<IConfig> = prepareBasicFields(config);

    updatedConfig.slots = prepareSlots(config.slots);
    updatedConfig.adServers = [...new Set(updatedConfig.slots.map((slot) => slot.adServer))];

    if (updatedConfig.prebidsConfig) {
        // informacja o wyłączonych bidderach (ustawiony priorytet "0")
        updatedConfig.prebidsConfig = {
            ...updatedConfig.prebidsConfig,
            disabledBidderNames: updatedConfig.prebidsConfig.priority
                .filter((prebidPriority) => !prebidPriority.priority)
                .map((prebidPriority) => prebidPriority.name.toLowerCase()),
        };
    }

    if (updatedConfig.adPath && SMART_LL[updatedConfig.adPath] && updatedConfig.slots.length) {
        const adPath = updatedConfig.adPath;
        const slotsSmart: string[] = Object.keys(SMART_LL[adPath]);
        slotsSmart.forEach((slotSmart: string) => {
            const half = updatedConfig.slots!.find((slot: ISlot) => slot.name === slotSmart);
            if (half) {
                const smartSlot: ISlot = { ...half, name: `${slotSmart}_${SMART_LL[adPath][slotSmart]}` };
                updatedConfig.slots!.push(smartSlot);
            }
        });
    }

    return prepareFullConfig({ ...updatedConfig });
};
